'use client';

import * as React from 'react';
import { useFirestore, useAuth } from '@/firebase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { GalleryBrowser } from './gallery-v2-browser';
import { AlbumTreeNavigator } from './gallery-v2-album-tree';
import { PhotoUploadDialog } from './gallery-v2-upload-dialog-enhanced';
import { CreateAlbumDialog } from './create-album-dialog';
import { getHybridRootAlbums, enrichAlbumsWithCovers, type HybridAlbum } from '@/lib/gallery-hybrid';
import { useCollection } from '@/firebase';
import { collection, query, Query, DocumentData, DocumentSnapshot, doc, getDoc } from 'firebase/firestore';
import type { YouTubeVideo } from '@/models/video';
import {
  Images as ImagesIcon,
  Video,
  Upload,
  Loader2,
  FolderTree,
  ArrowLeft,
  ChevronRight,
  Home,
  Pencil,
  Plane,
  X
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useRouter, useSearchParams } from 'next/navigation';
// import { hydratePhotonLinkAlbum } from '@/lib/gallery-actions';

// Breadcrumb item for navigation path tracking
interface BreadcrumbItem {
  id: string;
  name: string;
  source: 'firebase' | 'youtube';
  albumPath?: string;
}

export interface GalleryV2Props {
  /** URL path segments: [albumId, subAlbumId, ..., possibleImageId] */
  slugPath?: string[];
}

/**
 * Gallery v2 - Unified media gallery with Firebase integration
 */
export function GalleryV2({
  slugPath
}: GalleryV2Props) {
  const firestore = useFirestore();
  const auth = useAuth();
  const { toast } = useToast();
  const router = useRouter();
  const searchParams = useSearchParams();

  // Breadcrumb trail for navigation
  const [breadcrumbTrail, setBreadcrumbTrail] = React.useState<BreadcrumbItem[]>([]);
  const [showUploadDialog, setShowUploadDialog] = React.useState(false);
  const [viewMode, setViewMode] = React.useState<'grid' | 'masonry' | 'list'>('masonry');
  const [treeRefreshKey, setTreeRefreshKey] = React.useState(0);
  const [isDraggingOver, setIsDraggingOver] = React.useState(false);
  const [droppedFiles, setDroppedFiles] = React.useState<File[]>([]);

  // Aircraft filter from URL
  const aircraftFilter = searchParams.get('aircraft');

  // Image ID from slug (last segment, detected during hydration)
  const [initialMediaId, setInitialMediaId] = React.useState<string | undefined>(undefined);

  // Current album is the last item in the breadcrumb trail
  const currentAlbum = breadcrumbTrail[breadcrumbTrail.length - 1];
  const selectedAlbumId = currentAlbum?.id;
  const selectedAlbumSource = currentAlbum?.source;
  const [showCreateAlbumDialog, setShowCreateAlbumDialog] = React.useState(false);

  // Check if user is a member (can upload)
  const canUpload = React.useMemo(() => {
    return auth.currentUser !== null;
  }, [auth.currentUser]);

  // Check if user is officer or admin (can create albums)
  const canCreateAlbum = React.useMemo(() => {
    return auth.currentUser !== null;
  }, [auth.currentUser]);

  const [isInitialized, setIsInitialized] = React.useState(false);

  // Build the URL path for a breadcrumb trail
  const buildGalleryPath = React.useCallback((trail: BreadcrumbItem[], mediaId?: string) => {
    const segments = trail.map(b => b.id);
    if (mediaId) segments.push(mediaId);
    const path = segments.length > 0 ? `/gallery/${segments.join('/')}` : '/gallery';
    const params = new URLSearchParams();
    if (aircraftFilter) params.set('aircraft', aircraftFilter);
    return params.toString() ? `${path}?${params.toString()}` : path;
  }, [aircraftFilter]);

  // Walk up parentAlbumId chain to build full hierarchy breadcrumb
  const buildHierarchyTrail = React.useCallback(async (albumId: string): Promise<BreadcrumbItem[]> => {
    if (!firestore) return [];
    const trail: BreadcrumbItem[] = [];
    let currentId: string | null = albumId;

    while (currentId) {
      try {
        const snap: DocumentSnapshot<DocumentData> = await getDoc(doc(firestore, 'albums', currentId));
        if (!snap.exists()) break;
        const albumData: DocumentData = snap.data()!;
        trail.unshift({
          id: currentId,
          name: (albumData.name as string) || 'Album',
          source: 'firebase',
          albumPath: albumData.albumPath as string | undefined
        });
        const nextId: string | null = (albumData.parentAlbumId as string | null) || null;
        currentId = nextId;
      } catch {
        break;
      }
    }

    return trail;
  }, [firestore]);

  // Hydrate breadcrumb trail from slug path on mount
  React.useEffect(() => {
    if (!firestore || isInitialized) return;

    const hydrateFromSlug = async () => {
      // Support legacy query-param URLs: redirect to path-based
      const legacyAlbumId = searchParams.get('albumId');
      if (legacyAlbumId) {
        if (searchParams.get('source') === 'youtube' && legacyAlbumId === 'youtube-playlists') {
          setBreadcrumbTrail([{ id: 'youtube-playlists', name: 'YouTube Playlists', source: 'youtube' }]);
          setIsInitialized(true);
          return;
        }
        router.replace(`/gallery/${legacyAlbumId}`, { scroll: false });
        return;
      }

      if (!slugPath || slugPath.length === 0) {
        setIsInitialized(true);
        return;
      }

      // Handle YouTube
      if (slugPath[0] === 'youtube-playlists') {
        setBreadcrumbTrail([{ id: 'youtube-playlists', name: 'YouTube Playlists', source: 'youtube' }]);
        setIsInitialized(true);
        return;
      }

      // The last slug segment is the target album (or media).
      // Find the deepest album, then check if the very last segment is a media ID.
      const lastSegment = slugPath[slugPath.length - 1];
      let targetAlbumId: string | null = null;

      // Check if last segment is an album
      const lastAlbumDoc = await getDoc(doc(firestore, 'albums', lastSegment)).catch(() => null);
      if (lastAlbumDoc?.exists()) {
        targetAlbumId = lastSegment;
      } else {
        // Last segment might be a media ID
        const mediaDoc = await getDoc(doc(firestore, 'media', lastSegment)).catch(() => null);
        if (mediaDoc?.exists()) {
          setInitialMediaId(lastSegment);
          // The album is the second-to-last segment
          if (slugPath.length >= 2) {
            targetAlbumId = slugPath[slugPath.length - 2];
          }
        } else if (slugPath.length >= 2) {
          // Unknown last segment — try second-to-last as album
          targetAlbumId = slugPath[slugPath.length - 2];
        }
      }

      if (targetAlbumId) {
        const trail = await buildHierarchyTrail(targetAlbumId);
        setBreadcrumbTrail(trail);
      }

      setIsInitialized(true);
    };

    hydrateFromSlug();
  }, [firestore, slugPath, isInitialized, buildHierarchyTrail, router, searchParams]);

  // Update URL when breadcrumb trail changes (after initialization)
  // Use history.replaceState to avoid triggering Next.js navigation (which can unmount/remount the component)
  React.useEffect(() => {
    if (!isInitialized) return;

    const newUrl = buildGalleryPath(breadcrumbTrail);
    window.history.replaceState(window.history.state, '', newUrl);
  }, [breadcrumbTrail, isInitialized, buildGalleryPath]);

  // Handle album selection — build full hierarchy from the selected album
  const handleAlbumSelect = async (album: HybridAlbum | { id: string; source: 'youtube' }) => {
    if ('source' in album && album.source === 'youtube') {
      setBreadcrumbTrail([{ id: album.id, name: 'YouTube Playlists', source: 'youtube' }]);
      return;
    }

    const trail = await buildHierarchyTrail(album.id);
    if (trail.length === 0) {
      // Fallback if hierarchy lookup failed
      setBreadcrumbTrail([{
        id: album.id,
        name: 'name' in album ? album.name : 'Album',
        source: 'firebase',
        albumPath: 'path' in album ? album.path : undefined
      }]);
    } else {
      setBreadcrumbTrail(trail);
    }
  };

  // Handle lightbox media change — update URL with image ID
  // Use history.replaceState to avoid triggering Next.js navigation
  const handleLightboxMediaChange = React.useCallback((mediaId: string | null) => {
    const newUrl = buildGalleryPath(breadcrumbTrail, mediaId || undefined);
    window.history.replaceState(window.history.state, '', newUrl);
  }, [breadcrumbTrail, buildGalleryPath]);

  // Handle breadcrumb click - navigate to specific level
  const handleBreadcrumbClick = (index: number) => {
    if (index === -1) {
      // Home - clear all breadcrumbs
      setBreadcrumbTrail([]);
    } else {
      // Navigate to specific breadcrumb - trim trail to that level
      setBreadcrumbTrail(prev => prev.slice(0, index + 1));
    }
  };

  // Handle back to albums (go up one level)
  const handleBackToAlbums = () => {
    setBreadcrumbTrail(prev => prev.slice(0, -1));
  };

  // Handle upload complete
  const handleUploadComplete = () => {
    toast({
      title: 'Upload successful',
      description: 'Your photos have been uploaded to the gallery',
    });
    setShowUploadDialog(false);
    setDroppedFiles([]);
  };

  const isViewingAlbum = selectedAlbumId && selectedAlbumSource;

  // Drag and Drop Handlers
  const handleDragOver = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canUpload) return;

    // Check if dragging files
    if (e.dataTransfer.types.includes('Files')) {
      setIsDraggingOver(true);
    }
  }, [canUpload]);

  const handleDragLeave = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if we're leaving the main container
    // This is a bit tricky since events bubble up from children
    // But for now let's just use a simple approach
    if (e.currentTarget === e.target) {
      setIsDraggingOver(false);
    }
  }, []);

  // Use a ref to track drag enter/leave depth to avoid flickering
  const dragCounter = React.useRef(0);

  const handleDragEnter = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canUpload) return;

    if (e.dataTransfer.types.includes('Files')) {
      dragCounter.current += 1;
      setIsDraggingOver(true);
    }
  }, [canUpload]);

  const handleDragLeaveContainer = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      setIsDraggingOver(false);
    }
  }, []);

  const handleDrop = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
    dragCounter.current = 0;

    if (!canUpload) return;

    const files = Array.from(e.dataTransfer.files).filter(
      file => file.type.startsWith('image/') || file.name.endsWith('.zip')
    );

    if (files.length > 0) {
      setDroppedFiles(files);
      setShowUploadDialog(true);
    }
  }, [canUpload]);

  // Removed handleMakeEditable logic

  return (
    <div
      className="w-full max-w-7xl mx-auto p-4 md:p-8 min-h-screen"
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeaveContainer}
      onDrop={handleDrop}
    >
      {/* Header */}
      <Card className="mb-8">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-3xl">
                <ImagesIcon className="w-8 h-8 text-primary" />
                Club Gallery
              </CardTitle>
              <CardDescription>
                Browse photos and videos from our club members
              </CardDescription>
            </div>
            <div className="flex items-center">
              {canCreateAlbum && (
                <Button onClick={() => setShowCreateAlbumDialog(true)} variant="outline" className="mr-2">
                  <FolderTree className="w-4 h-4 mr-2" />
                  {currentAlbum ? 'Create Subalbum' : 'Create Album'}
                </Button>
              )}
              {canUpload && (
                <Button onClick={() => setShowUploadDialog(true)}>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Photos
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar - Album Tree (hidden on mobile) */}
        <div className="hidden lg:block lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <FolderTree className="w-5 h-5" />
                Albums
              </CardTitle>
            </CardHeader>
            <CardContent>
              {firestore ? (
                <AlbumTreeNavigator
                  selectedAlbumId={selectedAlbumId}
                  onAlbumSelect={handleAlbumSelect}
                  autoExpandDepth={1}
                  showMediaCounts={true}
                  useLinks={false}
                  refreshKey={treeRefreshKey}
                />
              ) : (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Main content area */}
        <div className="lg:col-span-3">
          {/* Breadcrumb navigation */}
          {breadcrumbTrail.length > 0 && (
            <div className="mb-4 flex items-center gap-2 text-sm flex-wrap">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleBreadcrumbClick(-1)}
                className="h-8 px-2"
              >
                <Home className="w-4 h-4" />
              </Button>

              {breadcrumbTrail.map((item, index) => (
                <React.Fragment key={`${item.id}-${index}`}>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  <Button
                    variant={index === breadcrumbTrail.length - 1 ? "secondary" : "ghost"}
                    size="sm"
                    onClick={() => handleBreadcrumbClick(index)}
                    className="h-8 px-3"
                    disabled={index === breadcrumbTrail.length - 1}
                  >
                    {item.name}
                  </Button>
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Aircraft Filter Banner */}
          {aircraftFilter && (
            <Card className="mb-4 bg-primary/5 border-primary/20">
              <CardContent className="py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Plane className="h-4 w-4 text-primary" />
                  <span className="text-sm">
                    Showing photos tagged with: <strong>{aircraftFilter}</strong>
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    // Remove aircraft filter - rebuild path without it
                    const segments = breadcrumbTrail.map(b => b.id);
                    const path = segments.length > 0 ? `/gallery/${segments.join('/')}` : '/gallery';
                    router.push(path, { scroll: false });
                  }}
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear filter
                </Button>
              </CardContent>
            </Card>
          )}

          {currentAlbum ? (
            <>
              {/* Show videos or photos based on source */}
              {selectedAlbumSource === 'youtube' ? (
                <YouTubePlaylistsView />
              ) : (
                firestore && (
                  <GalleryBrowser
                    albumId={selectedAlbumId}
                    albumSource={'firebase'}
                    aircraftModelFilter={aircraftFilter || undefined}
                    viewMode={viewMode}
                    sortBy="newest"
                    showViewModeSelector={true}
                    pageSize={50}
                    onAlbumClick={handleAlbumSelect}
                    onAlbumChange={() => setTreeRefreshKey(k => k + 1)}
                    isDraggingOver={isDraggingOver}
                    initialMediaId={initialMediaId}
                    onLightboxMediaChange={handleLightboxMediaChange}
                  />
                )
              )}
            </>
          ) : aircraftFilter ? (
            // Show filtered media across all albums when aircraft filter is active
            firestore && (
              <GalleryBrowser
                aircraftModelFilter={aircraftFilter}
                viewMode={viewMode}
                sortBy="newest"
                showViewModeSelector={true}
                pageSize={50}
                onAlbumClick={handleAlbumSelect}
                onAlbumChange={() => setTreeRefreshKey(k => k + 1)}
                isDraggingOver={isDraggingOver}
              />
            )
          ) : (
            // Default view - all albums grid
            <AlbumsGridView onAlbumSelect={handleAlbumSelect} />
          )}
        </div>
      </div>

      {/* Upload dialog */}
      <PhotoUploadDialog
        open={showUploadDialog}
        onClose={() => setShowUploadDialog(false)}
        defaultAlbumId={selectedAlbumId}
        initialFiles={droppedFiles}
        onUploadComplete={handleUploadComplete}
      />

      <CreateAlbumDialog
        open={showCreateAlbumDialog}
        onClose={() => setShowCreateAlbumDialog(false)}
        parentId={selectedAlbumId}
        parentPath={currentAlbum?.albumPath || null}
        onComplete={() => {
          setShowCreateAlbumDialog(false);
          // Trigger refresh via key
          setTreeRefreshKey(k => k + 1);
        }}
      />
    </div>
  );
}

/**
 * YouTube Playlists view
 */
function YouTubePlaylistsView() {
  const firestore = useFirestore();
  const { toast } = useToast();

  const videosRef = React.useMemo(() => {
    if (!firestore) return null;
    return collection(firestore, 'gallery/videoSources/videos');
  }, [firestore]);

  const { data: videos, loading, error } = useCollection<YouTubeVideo>(
    videosRef as Query<YouTubeVideo, DocumentData> | null
  );

  React.useEffect(() => {
    if (error) {
      toast({
        variant: 'destructive',
        title: 'Error loading videos',
        description: error.message,
      });
    }
  }, [error, toast]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="ml-4 text-muted-foreground">Loading Videos...</p>
      </div>
    );
  }

  if (!videos || videos.length === 0) {
    return (
      <div className="text-center py-20 text-muted-foreground">
        <Video className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>No videos have been added yet.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {videos.map((video) => (
        <Card key={video.id}>
          <CardHeader>
            <CardTitle className="text-lg">{video.title}</CardTitle>
            <CardDescription>
              By: <a href={video.channelUrl} target="_blank" rel="noopener noreferrer" className="hover:underline">
                {video.channelName}
              </a>
            </CardDescription>
          </CardHeader>
          <CardContent>
            {video.sourceType === 'channel_streams' ? (
              <a
                href={video.sourceUrl || video.embedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center aspect-video w-full overflow-hidden rounded-lg border bg-muted hover:bg-muted/80 transition-colors"
              >
                <div className="text-center p-8">
                  <Video className="w-12 h-12 mx-auto mb-4 text-primary" />
                  <p className="font-semibold mb-2">View Channel Streams</p>
                  <p className="text-sm text-muted-foreground">Click to open in YouTube</p>
                </div>
              </a>
            ) : (
              <div className="aspect-video w-full overflow-hidden rounded-lg border">
                <iframe
                  src={video.embedUrl}
                  width="100%"
                  height="100%"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  title={video.title}
                  className="border-0"
                />
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/**
 * Albums grid view with YouTube Playlists
 */
function AlbumsGridView({ onAlbumSelect }: { onAlbumSelect: (album: HybridAlbum | { id: string; source: 'youtube' }) => void }) {
  const firestore = useFirestore();
  const [albums, setAlbums] = React.useState<HybridAlbum[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!firestore) return;

    const fetchAlbums = async () => {
      try {
        const rootAlbums = await getHybridRootAlbums(firestore);
        // Enrich albums with cover images from sub-albums if needed
        const enrichedAlbums = await enrichAlbumsWithCovers(firestore, rootAlbums);
        setAlbums(enrichedAlbums);
      } catch (error) {
        console.error('Error fetching albums:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAlbums();
  }, [firestore]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
      {/* Regular albums */}
      {albums.map((album) => (
        <Card
          key={album.id}
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => onAlbumSelect(album)}
        >
          {album.coverUrl && (
            <div className="relative aspect-video overflow-hidden rounded-t-lg">
              <img
                src={album.coverUrl}
                alt={album.name}
                className="object-cover w-full h-full"
              />
            </div>
          )}
          <CardHeader>
            <CardTitle className="text-lg">{album.name}</CardTitle>
            {album.description && (
              <CardDescription>{album.description}</CardDescription>
            )}
            {album.mediaCount > 0 && (
              <CardDescription className="text-xs">
                {album.mediaCount} {album.mediaCount === 1 ? 'item' : 'items'}
              </CardDescription>
            )}
          </CardHeader>
        </Card>
      ))}

      {/* YouTube Playlists album */}
      <Card
        className="cursor-pointer hover:shadow-lg transition-shadow"
        onClick={() => onAlbumSelect({ id: 'youtube-playlists', source: 'youtube' })}
      >
        <div className="relative aspect-video overflow-hidden rounded-t-lg bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center">
          <Video className="w-16 h-16 text-white opacity-90" />
        </div>
        <CardHeader>
          <CardTitle className="text-lg">YouTube Playlists</CardTitle>
          <CardDescription>Club videos and live streams</CardDescription>
        </CardHeader>
      </Card>
    </div>
  );
}
